import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import Navbar from "@/components/Navbar";
import { useParams } from 'react-router-dom';
import { ArrowLeft, Calendar, Clock, Share2 } from "lucide-react";
import { Link } from 'react-router-dom';
import viictus from "@/assets/blog/viictus.jpg";
import cloud from "@/assets/blog/cloud.jpg";
import thermal1 from "@/assets/blog/thermal1.jpg";

const BlogPost = () => {
  const { slug } = useParams();

  // Special YouTube post
  if (slug === 'Upgrading-hp-victus') {
    return (
      <>
        <Navbar />
        <main className="min-h-screen bg-background w-full overflow-x-hidden">
          {/* Hero Section */}
          <section className="relative w-full h-[60vh] min-h-[400px] flex items-end">
            <img
              src= {viictus}
              alt="HP Victus RAM Upgrade"
              className="absolute inset-0 w-full h-full object-cover z-0"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/20 to-background z-10" />
            <div className="relative z-20 w-full max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pb-10 flex flex-col gap-4">
              <Link to="/blog" className="mb-2 self-start">
                <Button variant="outline" size="sm" className="gap-2">
                  <ArrowLeft className="h-4 w-4" />
                  Back to Blog
                </Button>
              </Link>
              <Badge className="w-fit mb-2 bg-blue-600 text-foreground">Tech</Badge>
              <h1 className="text-4xl sm:text-5xl font-extrabold text-foreground drop-shadow-sm mb-2">Upgrading HP Victus Laptop fb0147AX: RAM Upgrade 8GB to 20GB</h1>
              <div className="flex flex-wrap items-center gap-4 text-foreground/90 text-base">
                <div className="flex items-center gap-2">
                  <img src="https://avatars.githubusercontent.com/u/134158954?v=4" alt="Mohan Balaji" className="w-9 h-9 rounded-full border-2 border-primary shadow-md" />
                  <span className="font-semibold">Mohan Balaji</span>
                </div>
                <span className="hidden sm:inline">---</span>
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4" />
                  <span>June 2024</span>
                </div>
                <span className="hidden sm:inline">---</span>
                <div className="flex items-center gap-2">
                  <Clock className="h-4 w-4" />
                  <span>4 min read</span>
                </div>
              </div>
            </div>
          </section>

          {/* Article Content */}
          <section className="w-full max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
            <article className="prose prose-invert max-w-none">
              <h2 className="text-2xl font-bold text-primary">About This Upgrade</h2>
              <p>
                In this video and guide, I'm upgrading my HP Victus (fb0147AX/fb0184AX) gaming laptop, powered by AMD Ryzen 5 5600H and AMD Radeon RX 6500 Graphics, by increasing the RAM from 8GB to 20GB. I replace one of the 4GB RAM sticks with a 16GB Crucial DDR4 module for improved performance. Watch the full step-by-step guide below!
              </p>

              {/* Video Walkthrough */}
              <h2 className="text-2xl font-bold text-primary mt-10 mb-4">Video Walkthrough</h2>
              <div className="flex justify-center mb-10">
                <iframe
                  width="100%"
                  height="400"
                  src="https://www.youtube.com/embed/NqrwJXkvaZ4"
                  title="HP Victus RAM Upgrade - Step-by-Step"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  className="rounded-xl shadow-sm w-full max-w-2xl"
                ></iframe>
              </div>

              <h3 className="text-xl font-semibold mt-8">---- Laptop Specifications</h3>
              <ul className="list-disc ml-6">
                <li><b>Model:</b> HP Victus (fb0147AX/fb0184AX)</li>
                <li><b>Processor:</b> AMD Ryzen 5 5600H</li>
                <li><b>Graphics:</b> AMD Radeon RX 6500</li>
                <li><b>RAM (Before):</b> 8GB DDR4 (2 x 4GB)</li>
                <li><b>RAM (After):</b> 20GB DDR4 (1 x 4GB + 1 x 16GB)</li>
                <li><b>Storage:</b> 512GB SSD</li>
                <li><b>Display:</b> 15.6-inch (39.6cm) FHD IPS</li>
                <li><b>Cooling:</b> Enhanced Cooling System</li>
                <li><b>Operating System:</b> Windows 11 + MS Office 2021</li>
                <li><b>Weight:</b> 2.37kg</li>
                <li><b>Color:</b> Blue</li>
              </ul>

              <h2 className="text-2xl font-bold text-primary mt-10">Step-by-Step RAM Upgrade</h2>
              <ol className="list-decimal ml-6">
                <li>Power off your laptop and unplug all cables.</li>
                <li>Remove the back panel using a precision screwdriver.</li>
                <li>Locate the RAM slots. Carefully remove one 4GB stick.</li>
                <li>Insert the new 16GB Crucial DDR4 RAM module into the empty slot.</li>
                <li>Reattach the back panel and power on the laptop.</li>
                <li>Check system properties to confirm the RAM upgrade (should show 20GB).</li>
              </ol>

              <div className="bg-blue-900/30 border-l-4 border-blue-400 rounded p-4 my-6">
                <b>Tip:</b> Always ground yourself before handling RAM to avoid static damage. Use compatible RAM for best results.
              </div>

              <h3 className="text-xl font-semibold mt-8">Performance Benefits</h3>
              <ul className="list-disc ml-6">
                <li>Smoother multitasking and gaming</li>
                <li>Better performance in memory-intensive applications</li>
                <li>Improved system responsiveness</li>
                <li>Future-proofing your laptop for demanding tasks</li>
              </ul>

              <h3 className="text-xl font-semibold mt-8">Conclusion</h3>
              <p>
                Upgrading your HP Victus laptop's RAM is a cost-effective way to boost performance for gaming, productivity, and everyday use. If you found this guide helpful, check out the video for a full walkthrough and subscribe for more tech tips!
              </p>
            </article>
          </section>

          {/* Tags */}
          <section className="w-full max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pb-12">
            <div className="flex flex-wrap gap-2">
              <Badge variant="secondary" className="text-sm">#HPVictus</Badge>
              <Badge variant="secondary" className="text-sm">#RAMUpgrade</Badge>
              <Badge variant="secondary" className="text-sm">#LaptopUpgrade</Badge>
              <Badge variant="secondary" className="text-sm">#Ryzen5</Badge>
              <Badge variant="secondary" className="text-sm">#Tech</Badge>
            </div>
          </section>

          {/* Footer */}
          <footer className="w-full px-4 sm:px-6 lg:px-8 py-8 border-t border-border">
            <div className="w-full max-w-7xl mx-auto text-center">
              <p className="text-sm text-muted-foreground">
                -- {new Date().getFullYear()} All rights reserved.
              </p>
            </div>
          </footer>
        </main>
      </>
    );
  }

  // Special Thermal Paste Replacement post
  if (slug === 'laptop-thermal-paste-replacement') {
    return (
      <>
        <Navbar />
        <main className="min-h-screen bg-background w-full overflow-x-hidden">
          {/* Hero Section */}
          <section className="relative w-full h-[60vh] min-h-[400px] flex items-end">
            <img
              src={thermal1}
              alt="Laptop Thermal Paste Replacement"
              className="absolute inset-0 w-full h-full object-cover z-0"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/40 to-background z-10" />
            <div className="relative z-20 w-full max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pb-10 flex flex-col gap-4">
              <Link to="/blog" className="mb-2 self-start">
                <Button variant="outline" size="sm" className="gap-2">
                  <ArrowLeft className="h-4 w-4" />
                  Back to Blog
                </Button>
              </Link>
              <Badge className="w-fit mb-2 bg-orange-600 text-foreground">Tech</Badge>
              <h1 className="text-4xl sm:text-5xl font-extrabold text-foreground drop-shadow-sm mb-2">Laptop Thermal Paste Replacement: Fix Overheating Issues</h1>
              <div className="flex flex-wrap items-center gap-4 text-foreground/90 text-base">
                <div className="flex items-center gap-2">
                  <img src="https://avatars.githubusercontent.com/u/134158954?v=4" alt="Mohan Balaji" className="w-9 h-9 rounded-full border-2 border-primary shadow-md" />
                  <span className="font-semibold">Mohan Balaji</span>
                </div>
                <span className="hidden sm:inline">---</span>
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4" />
                  <span>January 2025</span>
                </div>
                <span className="hidden sm:inline">---</span>
                <div className="flex items-center gap-2">
                  <Clock className="h-4 w-4" />
                  <span>6 min read</span>
                </div>
              </div>
            </div>
          </section>

          {/* Article Content */}
          <section className="w-full max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
            <article className="prose prose-invert max-w-none">
              <h2 className="text-2xl font-bold text-primary">Why Replace Thermal Paste?</h2>
              <p>
                <span className="inline-block bg-orange-900/30 border-l-4 border-orange-400 px-3 py-2 rounded mb-2 font-medium">Overheating is one of the most common laptop issues that can severely impact performance and longevity. Thermal paste, also known as thermal compound, plays a crucial role in heat transfer between your CPU/GPU and the cooling system. Over time, it dries out, becomes less effective, and can cause thermal throttling, random shutdowns, and reduced performance.</span>
              </p>

              <h3 className="text-xl font-semibold mt-8">Signs You Need Thermal Paste Replacement</h3>
              <ul className="list-disc ml-6">
                <li><b>High CPU/GPU temperatures</b> (above 85--C under load)</li>
                <li><b>Thermal throttling</b> - CPU/GPU reducing performance to cool down</li>
                <li><b>Random shutdowns</b> during intensive tasks</li>
                <li><b>Loud fan noise</b> - fans running at maximum speed constantly</li>
                <li><b>Performance degradation</b> over time</li>
                <li><b>Laptop getting hot</b> to the touch during normal use</li>
              </ul>

              <h2 className="text-2xl font-bold text-primary mt-10">Tools & Materials Needed</h2>
              <div className="bg-blue-900/30 border-l-4 border-blue-400 rounded p-4 my-6">
                <b>Safety First:</b> Always work on a clean, static-free surface and consider using an anti-static wristband to prevent ESD damage.
              </div>
              <h4 className="text-lg font-semibold mt-6">Essential Tools</h4>
              <ul className="list-disc ml-6">
                <li>Precision screwdriver set (Phillips #0, #1, #2)</li>
                <li>Plastic pry tools or guitar picks</li>
                <li>Isopropyl alcohol (90%+ concentration)</li>
                <li>Lint-free cloths or coffee filters</li>
                <li>Thermal paste (Arctic MX-4, Noctua NT-H1, or Thermal Grizzly Kryonaut recommended)</li>
                <li>Anti-static wristband (optional but recommended)</li>
              </ul>

              <h4 className="text-lg font-semibold mt-6">Best Thermal Pastes for Laptops</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 my-6">
                <div className="bg-gray-800/50 p-4 rounded-lg border border-border">
                  <h5 className="font-semibold text-foreground mb-2">Arctic MX-4</h5>
                  <p className="text-sm text-muted-foreground">Excellent performance, easy to apply, non-conductive, long-lasting (8+ years)</p>
                </div>
                <div className="bg-gray-800/50 p-4 rounded-lg border border-border">
                  <h5 className="font-semibold text-foreground mb-2">Noctua NT-H1</h5>
                  <p className="text-sm text-muted-foreground">High-end performance, excellent for gaming laptops, easy cleanup</p>
                </div>
                <div className="bg-gray-800/50 p-4 rounded-lg border border-border">
                  <h5 className="font-semibold text-foreground mb-2">Thermal Grizzly Kryonaut</h5>
                  <p className="text-sm text-muted-foreground">Premium performance, best for high-end laptops, excellent thermal conductivity</p>
                </div>
                <div className="bg-gray-800/50 p-4 rounded-lg border border-border">
                  <h5 className="font-semibold text-foreground mb-2">Cooler Master MasterGel</h5>
                  <p className="text-sm text-muted-foreground">Budget-friendly, good performance, easy to apply</p>
                </div>
              </div>

              <h2 className="text-2xl font-bold text-primary mt-10">Step-by-Step Replacement Guide</h2>
              <div className="bg-red-900/30 border-l-4 border-red-400 rounded p-4 my-6">
                <b>Warning:</b> This process will void your warranty. Only proceed if you're comfortable with laptop disassembly. If unsure, consult a professional.
              </div>

              <h4 className="text-lg font-semibold mt-6">Step 1: Preparation</h4>
              <ol className="list-decimal ml-6">
                <li>Power off your laptop completely and unplug all cables</li>
                <li>Remove the battery if possible (some laptops have non-removable batteries)</li>
                <li>Ground yourself to prevent static discharge</li>
                <li>Work in a clean, well-lit area</li>
              </ol>

              <h4 className="text-lg font-semibold mt-6">Step 2: Disassembly</h4>
              <ol className="list-decimal ml-6">
                <li>Remove all screws from the bottom panel (keep them organized by location)</li>
                <li>Use plastic pry tools to carefully separate the bottom panel from the chassis</li>
                <li>Disconnect any ribbon cables or connectors carefully</li>
                <li>Locate the CPU and GPU heatsinks</li>
              </ol>

              <h4 className="text-lg font-semibold mt-6">Step 3: Remove Old Thermal Paste</h4>
              <ol className="list-decimal ml-6">
                <li>Unscrew the heatsink mounting screws in a crisscross pattern</li>
                <li>Gently lift the heatsink away from the CPU/GPU</li>
                <li>Clean both the CPU/GPU die and heatsink base with isopropyl alcohol</li>
                <li>Use lint-free cloths to remove all traces of old thermal paste</li>
                <li>Ensure both surfaces are completely clean and dry</li>
              </ol>

              <h4 className="text-lg font-semibold mt-6">Step 4: Apply New Thermal Paste</h4>
              <ol className="list-decimal ml-6">
                <li>Apply a small pea-sized amount of thermal paste to the center of the CPU die</li>
                <li>For GPU, apply a slightly larger amount (about the size of a grain of rice)</li>
                <li>Do NOT spread the paste manually - let the heatsink pressure distribute it</li>
                <li>Ensure even coverage without over-application</li>
              </ol>

              <h4 className="text-lg font-semibold mt-6">Step 5: Reassembly</h4>
              <ol className="list-decimal ml-6">
                <li>Carefully place the heatsink back onto the CPU/GPU</li>
                <li>Tighten screws in a crisscross pattern to ensure even pressure</li>
                <li>Reconnect all ribbon cables and connectors</li>
                <li>Replace the bottom panel and secure all screws</li>
                <li>Reinstall the battery if removed</li>
              </ol>

              <h2 className="text-2xl font-bold text-primary mt-10">Testing & Verification</h2>
              <h4 className="text-lg font-semibold mt-6">Temperature Monitoring</h4>
              <p>After reassembly, monitor your laptop's temperatures using these tools:</p>
              <ul className="list-disc ml-6">
                <li><b>HWiNFO64</b> - Comprehensive hardware monitoring</li>
                <li><b>Core Temp</b> - CPU temperature monitoring</li>
                <li><b>MSI Afterburner</b> - GPU temperature monitoring</li>
                <li><b>ThrottleStop</b> - CPU monitoring and undervolting</li>
              </ul>

              <h4 className="text-lg font-semibold mt-6">Expected Results</h4>
              <ul className="list-disc ml-6">
                <li>CPU temperatures should be 10-20--C lower under load</li>
                <li>Reduced fan noise during normal operation</li>
                <li>Elimination of thermal throttling</li>
                <li>Improved performance in intensive tasks</li>
                <li>Stable operation without random shutdowns</li>
              </ul>

              <div className="bg-green-900/30 border-l-4 border-green-400 rounded p-4 my-6">
                <b>Pro Tip:</b> Run a stress test (like Prime95 or FurMark) for 10-15 minutes to verify the thermal paste is working correctly. Monitor temperatures throughout the test.
              </div>

              <h3 className="text-xl font-semibold mt-8">When to Replace Again</h3>
              <p>Thermal paste typically lasts 2-5 years depending on usage and quality. Replace it when you notice:</p>
              <ul className="list-disc ml-6">
                <li>Temperatures gradually increasing over time</li>
                <li>Performance degradation after 2+ years</li>
                <li>After major hardware changes or upgrades</li>
                <li>If you experience overheating issues again</li>
              </ul>

              <h3 className="text-xl font-semibold mt-8">Common Mistakes to Avoid</h3>
              <ul className="list-disc ml-6">
                <li><b>Using too much thermal paste</b> - Can actually increase temperatures</li>
                <li><b>Not cleaning thoroughly</b> - Old paste residue reduces effectiveness</li>
                <li><b>Overtightening screws</b> - Can damage the CPU/GPU die</li>
                <li><b>Using conductive thermal paste incorrectly</b> - Can cause short circuits</li>
                <li><b>Not testing after replacement</b> - Always verify temperatures</li>
              </ul>

              <h3 className="text-xl font-semibold mt-8">Conclusion</h3>
              <p>
                Replacing thermal paste is a cost-effective way to restore your laptop's cooling performance and extend its lifespan. With the right tools, patience, and attention to detail, you can significantly improve your laptop's thermal performance and prevent overheating issues.
              </p>
              <p>
                Remember: if you're not comfortable with laptop disassembly, it's always better to consult a professional technician. The cost of professional service is often worth avoiding potential damage to your expensive laptop components.
              </p>
            </article>
          </section>

          {/* Tags */}
          <section className="w-full max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pb-12">
            <div className="flex flex-wrap gap-2">
              <Badge variant="secondary" className="text-sm">#ThermalPaste</Badge>
              <Badge variant="secondary" className="text-sm">#LaptopRepair</Badge>
              <Badge variant="secondary" className="text-sm">#Overheating</Badge>
              <Badge variant="secondary" className="text-sm">#Cooling</Badge>
              <Badge variant="secondary" className="text-sm">#Tech</Badge>
            </div>
          </section>

          {/* Footer */}
          <footer className="w-full px-4 sm:px-6 lg:px-8 py-8 border-t border-border">
            <div className="w-full max-w-7xl mx-auto text-center">
              <p className="text-sm text-muted-foreground">
                -- {new Date().getFullYear()} All rights reserved.
              </p>
            </div>
          </footer>
        </main>
      </>
    );
  }

  // Special Homelab Cloud Storage post
  if (slug === 'homelab-cloud-truenas-cloudflare') {
    return (
      <>
        <Navbar />
        <main className="min-h-screen bg-background w-full overflow-x-hidden">
          {/* Hero Section */}
          <section className="relative w-full h-[60vh] min-h-[400px] flex items-end">
            <img
              src={cloud}
              alt="Homelab Cloud Storage with TrueNAS & Cloudflare"
              className="absolute inset-0 w-full h-full object-cover z-0"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/40 to-background z-10" />
            <div className="relative z-20 w-full max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pb-10 flex flex-col gap-4">
              <Link to="/blog" className="mb-2 self-start">
                <Button variant="outline" size="sm" className="gap-2">
                  <ArrowLeft className="h-4 w-4" />
                  Back to Blog
                </Button>
              </Link>
              <Badge className="w-fit mb-2 bg-blue-600 text-foreground">Tech</Badge>
              <h1 className="text-4xl sm:text-5xl font-extrabold text-foreground drop-shadow-sm mb-2">Homelab Cloud Storage with TrueNAS & Cloudflare</h1>
              <div className="flex flex-wrap items-center gap-4 text-foreground/90 text-base">
                <div className="flex items-center gap-2">
                  <img src="https://avatars.githubusercontent.com/u/134158954?v=4" alt="Mohan Balaji" className="w-9 h-9 rounded-full border-2 border-primary shadow-md" />
                  <span className="font-semibold">Mohan Balaji</span>
                </div>
                <span className="hidden sm:inline">---</span>
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4" />
                  <span>June 2025</span>
                </div>
                <span className="hidden sm:inline">---</span>
                <div className="flex items-center gap-2">
                  <Clock className="h-4 w-4" />
                  <span>8 min read</span>
                </div>
              </div>
            </div>
          </section>

          {/* Article Content */}
          <section className="w-full max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
            <article className="prose prose-invert max-w-none">
              <h2 className="text-2xl font-bold text-primary">Why Build Your Own Homelab Cloud Storage?</h2>
              <p>
                <span className="inline-block bg-primary/10 border-l-4 border-primary px-3 py-2 rounded mb-2 font-medium">With privacy concerns and the rising cost of commercial cloud storage, many tech enthusiasts are turning to self-hosted solutions. TrueNAS Scale, combined with Cloudflare Tunnel, offers a powerful, secure, and cost-effective way to manage your own cloud storage at home.</span>
              </p>
              <h3 className="text-xl font-semibold mt-8">Key Features</h3>
              <ul className="list-disc ml-6">
                <li>Enterprise-grade storage with <b>ZFS</b> and <b>RAID</b></li>
                <li>Secure remote access via <b>Cloudflare Tunnel</b></li>
                <li>Automated backups and snapshots</li>
                <li>Media streaming and file sharing</li>
                <li>Containerized apps and plugin support</li>
              </ul>
              <h3 className="text-xl font-semibold mt-8">Hardware & Software Requirements</h3>
              <ul className="list-disc ml-6">
                <li>Old PC/server or NAS hardware (8GB+ RAM recommended)</li>
                <li>Multiple hard drives for redundancy (ZFS/RAID)</li>
                <li>TrueNAS Scale (free, open-source)</li>
                <li>Cloudflare account (free tier works)</li>
                <li>Basic networking knowledge</li>
              </ul>

              {/* Video Walkthrough */}
              <h2 className="text-2xl font-bold text-primary mt-10 mb-4">Video Walkthrough</h2>
              <div className="flex justify-center mb-10">
                <iframe
                  width="100%"
                  height="400"
                  src="https://www.youtube.com/embed/WGM9GrnDn1Q"
                  title="Homelab Cloud Storage with TrueNAS & Cloudflare - Walkthrough"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  className="rounded-xl shadow-sm w-full max-w-2xl"
                ></iframe>
              </div>

              <h2 className="text-2xl font-bold text-primary mt-10">Step-by-Step Setup</h2>
              <div className="bg-blue-900/30 border-l-4 border-blue-400 rounded p-4 my-6">
                <b>Tip:</b> Always back up your data before making major changes to your storage setup!
              </div>
              <h4 className="text-lg font-semibold mt-6">1. Install TrueNAS Scale</h4>
              <p>Download the ISO from the <a href="https://www.truenas.com/scale/" target="_blank" rel="noopener noreferrer">official site</a> and install it on your hardware. Follow the guided setup to configure your storage pool (ZFS recommended for snapshots and redundancy).</p>
              <h4 className="text-lg font-semibold mt-6">2. Configure Storage Pools & Shares</h4>
              <p>Create datasets for your files, media, and backups. Set up SMB or NFS shares for local network access.</p>
              <h4 className="text-lg font-semibold mt-6">3. Set Up Cloudflare Tunnel for Secure Remote Access</h4>
              <ol className="list-decimal ml-6">
                <li>Sign up at <a href="https://cloudflare.com" target="_blank" rel="noopener noreferrer">Cloudflare</a> and add your domain.</li>
                <li>Install <code>cloudflared</code> on your TrueNAS server:</li>
              </ol>
              <pre className="bg-black/80 text-foreground rounded p-4 overflow-x-auto"><code>curl -L https://github.com/cloudflare/cloudflared/releases/latest/download/cloudflared-linux-amd64.deb -o cloudflared.deb
sudo dpkg -i cloudflared.deb</code></pre>
              <ol className="list-decimal ml-6" start={3}>
                <li>Authenticate and create a tunnel:</li>
              </ol>
              <pre className="bg-black/80 text-foreground rounded p-4 overflow-x-auto"><code>cloudflared tunnel login
cloudflared tunnel create truenas-tunnel
cloudflared tunnel route dns truenas-tunnel storage.yourdomain.com</code></pre>
              <ol className="list-decimal ml-6" start={4}>
                <li>Run the tunnel as a service for persistent access.</li>
              </ol>
              <h4 className="text-lg font-semibold mt-6">4. Install Apps & Plugins</h4>
              <p>Use the TrueNAS Apps catalog to deploy Nextcloud, Plex, or other containers for media streaming, file sync, and more.</p>
              <h4 className="text-lg font-semibold mt-6">5. Set Up Automated Backups</h4>
              <p>Configure periodic snapshots and replication tasks to external drives or cloud targets for extra safety.</p>
              <div className="bg-green-900/30 border-l-4 border-green-400 rounded p-4 my-6">
                <b>Best Practice:</b> Enable 2FA on your Cloudflare account and monitor your storage health regularly.
              </div>
              <h3 className="text-xl font-semibold mt-8">Conclusion</h3>
              <p>
                Building your own homelab cloud storage with TrueNAS and Cloudflare gives you full control, privacy, and flexibility. Whether for backups, media, or personal projects, this setup is scalable and future-proof for tech enthusiasts.
              </p>
              <p>
                <a href="https://github.com/Mohan-Balaji/homelab-cloud" target="_blank" rel="noopener noreferrer" className="text-primary font-bold">View Source Code on GitHub</a>
              </p>
            </article>
          </section>

          {/* Tags */}
          <section className="w-full max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pb-12">
            <div className="flex flex-wrap gap-2">
              <Badge variant="secondary" className="text-sm">#TrueNAS</Badge>
              <Badge variant="secondary" className="text-sm">#Cloudflare</Badge>
              <Badge variant="secondary" className="text-sm">#Homelab</Badge>
              <Badge variant="secondary" className="text-sm">#SelfHosting</Badge>
              <Badge variant="secondary" className="text-sm">#Storage</Badge>
            </div>
          </section>

          {/* Footer */}
          <footer className="w-full px-4 sm:px-6 lg:px-8 py-8 border-t border-border">
            <div className="w-full max-w-7xl mx-auto text-center">
              <p className="text-sm text-muted-foreground">
                -- {new Date().getFullYear()} All rights reserved.
              </p>
            </div>
          </footer>
        </main>
      </>
    );
  }

  // This would typically come from your API or CMS
  const post = {
    title: "Exploring the Hidden Gems of Southeast Asia",
    description: "Discover the lesser-known destinations that offer authentic cultural experiences away from the typical tourist paths.",
    content: `
      <p>Southeast Asia is a treasure trove of hidden gems waiting to be discovered. From secluded beaches to ancient temples, the region offers countless opportunities for authentic cultural experiences away from the typical tourist paths.</p>
      
      <h2>Why Venture Off the Beaten Path?</h2>
      <p>While popular destinations like Bangkok and Bali offer their own charm, the true essence of Southeast Asia often lies in its lesser-known locations. These hidden gems provide:</p>
      <ul>
        <li>Authentic cultural interactions</li>
        <li>Unspoiled natural beauty</li>
        <li>More affordable experiences</li>
        <li>Fewer tourists</li>
      </ul>

      <h2>Must-Visit Hidden Gems</h2>
      <p>Here are some of our favorite off-the-beaten-path destinations:</p>
      <ol>
        <li>Kampong Ayer, Brunei</li>
        <li>Bantayan Island, Philippines</li>
        <li>Hsipaw, Myanmar</li>
        <li>Con Dao Islands, Vietnam</li>
      </ol>

      <h2>Tips for Exploring Hidden Gems</h2>
      <p>When venturing off the beaten path, keep these tips in mind:</p>
      <ul>
        <li>Research local customs and traditions</li>
        <li>Learn basic phrases in the local language</li>
        <li>Be respectful of local communities</li>
        <li>Support local businesses</li>
      </ul>

      <p>Remember, the best travel experiences often come from unexpected discoveries and genuine connections with local communities.</p>
    `,
    category: "Travel",
    author: {
      name: "Mohan Balaji",
      avatar: "https://avatars.githubusercontent.com/u/134158954?v=4",
      bio: "Travel writer and photographer with a passion for discovering hidden gems across Asia."
    },
    date: "May 12, 2025",
    readTime: "6 min read",
    image: "https://avatars.githubusercontent.com/u/134158954?v=4",
    tags: ["Travel", "Culture", "Adventure", "Asia"]
  };

  return (
    <>
      <Navbar />
      <main className="min-h-screen bg-background w-full overflow-x-hidden">
        {/* Hero Section */}
        <section className="relative w-full h-[60vh] min-h-[400px]">
          {/* Background Image */}
          <div className="absolute inset-0">
            <img
              src={post.image}
              alt={post.title}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/60 to-background"></div>
          </div>

          {/* Content */}
          <div className="relative h-full w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col justify-end pb-12">
            <Link to="/blog" className="mb-6">
              <Button variant="outline" size="sm" className="gap-2">
                <ArrowLeft className="h-4 w-4" />
                Back to Blog
              </Button>
            </Link>
            <Badge className="w-fit mb-4">{post.category}</Badge>
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-foreground mb-4 max-w-4xl">
              {post.title}
            </h1>
            <div className="flex flex-wrap items-center gap-4 text-foreground/80">
              <div className="flex items-center gap-3">
                <img
                  src={post.author.avatar}
                  alt={post.author.name}
                  className="w-10 h-10 rounded-full"
                />
                <span>{post.author.name}</span>
              </div>
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                <span>{post.date}</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="h-4 w-4" />
                <span>{post.readTime}</span>
              </div>
            </div>
          </div>
        </section>

        {/* Article Content */}
        <section className="w-full max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="prose prose-invert max-w-none">
            <div dangerouslySetInnerHTML={{ __html: post.content }} />
          </div>
        </section>

        {/* Author Bio */}
        <section className="w-full max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="bg-card p-6 sm:p-8 rounded-2xl border border-border shadow-sm">
            <div className="flex items-start gap-4 sm:gap-6">
              <img
                src={post.author.avatar}
                alt={post.author.name}
                className="w-16 h-16 sm:w-20 sm:h-20 rounded-full"
              />
              <div>
                <h3 className="text-lg sm:text-xl font-semibold text-foreground mb-2">About {post.author.name}</h3>
                <p className="text-muted-foreground">{post.author.bio}</p>
              </div>
            </div>
          </div>
        </section>

        {/* Share Section */}
       

        {/* Tags */}
        <section className="w-full max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pb-12">
          <div className="flex flex-wrap gap-2">
            {post.tags.map((tag) => (
              <Badge key={tag} variant="secondary" className="text-sm">
                #{tag}
              </Badge>
            ))}
          </div>
        </section>

        {/* Footer */}
        <footer className="w-full px-4 sm:px-6 lg:px-8 py-8 border-t border-border">
          <div className="w-full max-w-7xl mx-auto text-center">
            <p className="text-sm text-muted-foreground">
              -- {new Date().getFullYear()} All rights reserved.
            </p>
          </div>
        </footer>
      </main>
    </>
  );
};

export default BlogPost; 
